import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useMachine } from '@xstate/react';
import { ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import { useHistory } from 'react-router';
import { applicationParameters } from '../../utilities/application-parameters';
import { useSelectedProvider } from '../../components/App/ProviderContainer/useSelectedProvider';
import { useConfiguration } from '../../components/ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { messages as authorizationMessages } from '../../components/AuthorizationDirector/messages';
import { SuccessNotification } from '../../core/screens/SuccessNotification/SuccessNotification';
import { showSuccessScreenForAccountCheck } from '../../models/configuration';
import { User } from '../../models/user';
import { OauthStoreDescription } from '../../stores/oauth-store';
import { AppState } from '../../stores/redux-store';
import { ApplicationResult } from '../../utilities/post-notification-handler';
import { AccountDialogType, ReportType } from '../../reports/models/report';
import { RefreshableItem } from '../../models/refreshableItem';
import { messages as bundledFlowMessages } from '../../reports/components/BundledFlow/molecules/messages';
import { AccountList } from '../components/AccountList/AccountList';
import { LoadingOverlay } from '../../core/screens/LoadingOverlay/LoadingOverlay';
import { ApplicationError } from '../../components/ApplicationErrorBoundary/errors/ApplicationError';
import { ERR_REPORTS_FAILED_GENERATE_REPORT } from '../../reports/services/reports';
import { ReduxCredentials } from '../../components/screens/SubmitCredentials/ReduxCredentials';
import { useAnalyticsCredentialsUpdating } from '../../components/hooks/useAnalyticsCredentialsUpdating/useAnalyticsCredentialsUpdating';
import { useIdempotencyKey } from '../../reports/useIdempotencyKey';
import { createAccountCheckMachine } from './accountCheckMachine';
import { PermanentUserAccountCheckResult } from './components/PermanentUserAccountCheckResult';
import { TemporaryUserAccountCheckResult } from './components/TemporaryUserAccountCheckResult';

const NOT_IMPLEMENTED_MULTI_ACCOUNT_SELECTION_VIEW = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.UNEXPECTED_ERROR,
};

const accountDialogType = AccountDialogType.SINGLE;
const refreshableItems = [
  RefreshableItem.CHECKING_ACCOUNTS,
  RefreshableItem.SAVING_ACCOUNTS,
  RefreshableItem.IDENTITY_DATA,
];

const reportTypes = [ReportType.ACCOUNT_VERIFICATION_REPORT];
export type AccountCheckMachineViewProps = {
  onCancel: () => void;
  externalReference?: string;
};

// istanbul ignore next: this component is a switch.
export const AccountCheckMachineView: React.FC<AccountCheckMachineViewProps> = ({
  onCancel,
  externalReference,
}) => {
  useAnalyticsCredentialsUpdating();
  const { data: configurationData } = useConfiguration();

  const { user, credentials, oauthDescription } = useSelector((state: AppState) => {
    return {
      user: state.user.user,
      oauthDescription: state.oauth.description as OauthStoreDescription,
      credentials: state.credentials.data,
      redirectedCredentialsId: state.application.redirectedCredentialsId,
      accountVerificationReportId: state.accountCheck.data.id,
      accountCheckIsLoading: state.accountCheck.loading,
    };
  });

  const history = useHistory();
  const { selectedProvider } = useSelectedProvider();
  const idempotencyKey = useIdempotencyKey();

  const onCancelOrGoBack = () => {
    onCancel();
    // ReduxCredentials doesn't reliably call the onBack callback,
    // instead sometimes doing history.goBack by itself, so we're doing
    // the same to align until we fix the issue.
    // TODO: use send() to manage the navigation in XState
    history.goBack();
  };

  const machine = useMemo(
    () =>
      createAccountCheckMachine({
        accountDialogType,
        getAccountsPayload: {
          reportTypes,
          accountDialogType,
          refreshableItems,
        },
        generateReportsPayload: {
          customerReferenceId: undefined,
          reportTypes,
          accountDialogType,
          refreshableItems,
          externalReference,
        },
      }),
    [externalReference]
  );
  const [state, send] = useMachine(machine);

  const { accountsResponse, createReportResponse, error } = state.context;

  if (state.matches('auth')) {
    return (
      <ReduxCredentials
        onDone={() => {
          send('SET_IDEMPOTENCY_KEY', { data: idempotencyKey });
          send('CREDENTIALS');
        }}
        onBack={onCancelOrGoBack}
        // TODO: read the selected provider from machine context
        provider={selectedProvider}
        refreshableItems={applicationParameters.get('refreshableItems')}
      />
    );
  }

  if (state.matches('SUCCESS')) {
    const accountVerificationReport = createReportResponse!.find(
      (report) => report.reportType === ReportType.ACCOUNT_VERIFICATION_REPORT
    );

    if (!accountVerificationReport) {
      throw new ApplicationError(ERR_REPORTS_FAILED_GENERATE_REPORT);
    }
    const message = (
      <FormattedMessage
        {...authorizationMessages.accountConnectedTo}
        values={{ providerName: selectedProvider.displayName }}
      />
    );

    const skipSuccessScreen = !showSuccessScreenForAccountCheck(configurationData);

    let applicationSuccessResult: ApplicationResult;

    if (User.isTemporary(user)) {
      applicationSuccessResult = new TemporaryUserAccountCheckResult({
        accountVerificationReportId: accountVerificationReport.reportId,
      });
    } else {
      applicationSuccessResult = new PermanentUserAccountCheckResult({
        accountVerificationReportId: accountVerificationReport.reportId,
        credentialsId: credentials!.id,
      });
    }

    return (
      <SuccessNotification
        overlayHeading={message}
        applicationSuccessResult={applicationSuccessResult}
        forceSkipSuccessScreen={skipSuccessScreen}
      />
    );
  }

  if (state.matches('ERROR')) {
    throw error;
  }

  if (state.matches('SINGLE_ACCOUNT_SELECTION_VIEW')) {
    return (
      <AccountList
        title={<FormattedMessage {...bundledFlowMessages.selectYourAccount} />}
        subTitle={
          <FormattedMessage
            {...bundledFlowMessages.selectYourAccountSubHeader}
            values={{ clientName: oauthDescription.clientName }}
          />
        }
        allowSelectMultipleAccounts={false}
        accounts={accountsResponse!}
        providerDisplayName={selectedProvider.displayName}
        providerLogo={selectedProvider.images.icon}
        onBack={onCancelOrGoBack}
        onSubmit={(accountIds) => {
          send('EVENT_ACCOUNTS_SELECTED', {
            data: accountIds,
          });
        }}
      />
    );
  }

  if (state.matches('MULTIPLE_ACCOUNT_SELECTION_VIEW')) {
    throw new ApplicationError(NOT_IMPLEMENTED_MULTI_ACCOUNT_SELECTION_VIEW);
  }

  return <LoadingOverlay />;
};
